<template>
  <div class="policies-tab">
    <section class="panel">
      <div class="panel-header">
        <div>
          <h3>Policy versions</h3>
          <p>
            Newest first. A draft has no effect until it is published. Retiring a published version makes its scope fall back
            to less specific policies for future referrals; earlier versions of the same scope are not restored.
          </p>
        </div>
      </div>
      <el-form :inline="true" size="small" class="versions-filters" @submit.native.prevent="fetchVersions">
        <el-form-item label="Status">
          <el-select v-model="versionFilters.status" style="width: 130px" @change="fetchVersions">
            <el-option label="All" value="all" />
            <el-option label="Draft" value="draft" />
            <el-option label="Published" value="published" />
            <el-option label="Retired" value="retired" />
          </el-select>
        </el-form-item>
        <el-form-item label="Scope key">
          <el-input
            v-model.trim="versionFilters.scope_key"
            clearable
            placeholder="e.g. global, type:collection, affiliate:<id>"
            style="width: 320px"
            class="mono-input"
            @clear="fetchVersions"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="text" @click="useEditorScope">Use editor scope</el-button>
        </el-form-item>
        <el-form-item label="Limit">
          <el-select v-model="versionFilters.limit" style="width: 90px" @change="fetchVersions">
            <el-option v-for="limit in [50, 100, 200]" :key="limit" :label="limit" :value="limit" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button icon="el-icon-search" native-type="submit" :loading="versionsLoading">Search</el-button>
        </el-form-item>
      </el-form>

      <el-table
        v-loading="versionsLoading"
        :data="versions"
        border
        size="small"
        style="width: 100%"
        row-key="_id"
        class="versions-table"
        :row-class-name="versionRowClass"
        empty-text="No policy versions match these filters"
      >
        <el-table-column type="expand">
          <template slot-scope="scope">
            <div class="expand-body">
              <el-descriptions :column="1" size="small" border>
                <el-descriptions-item v-for="field in fieldRows(scope.row.fields)" :key="field.key" :label="field.label">
                  {{ field.value }}
                </el-descriptions-item>
              </el-descriptions>
              <div v-if="scope.row.retirement_reason" class="muted expand-note">Retirement reason: {{ scope.row.retirement_reason }}</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="Version" min-width="170">
          <template slot-scope="scope">
            <div class="source-cell">
              <span class="mono" :title="idOf(scope.row)">{{ shortId(scope.row, 12) }}</span>
              <el-button type="text" size="mini" icon="el-icon-document-copy" @click="copyText(idOf(scope.row), 'Policy ID copied')" />
            </div>
            <div class="muted">Created {{ formatTime(scope.row.created_at) }}</div>
          </template>
        </el-table-column>
        <el-table-column label="Scope" min-width="210">
          <template slot-scope="scope">
            <div class="strong">{{ scopeLabel(scope.row.scope) }}</div>
            <div class="muted mono">{{ scope.row.scope_key || '-' }}</div>
          </template>
        </el-table-column>
        <el-table-column label="Status" width="120">
          <template slot-scope="scope">
            <el-tag size="small" :type="versionStatusTag(scope.row.status)">{{ versionStatusLabel(scope.row.status) }}</el-tag>
            <div v-if="scope.row.status === 'published' && isFuture(scope.row.effective_at)" class="muted">Scheduled</div>
          </template>
        </el-table-column>
        <el-table-column label="Effective" width="160">
          <template slot-scope="scope">{{ formatTime(scope.row.effective_at, true) }}</template>
        </el-table-column>
        <el-table-column label="Fields" min-width="200">
          <template slot-scope="scope">
            <span class="cell-wrap">{{ fieldRows(scope.row.fields).map((field) => field.label).join(', ') || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Reason" min-width="200">
          <template slot-scope="scope">
            <div class="cell-wrap">{{ scope.row.reason || '-' }}</div>
            <div v-if="scope.row.published_at" class="muted">Published {{ formatTime(scope.row.published_at) }}</div>
            <div v-if="scope.row.retired_at" class="muted">Retired {{ formatTime(scope.row.retired_at) }}</div>
          </template>
        </el-table-column>
        <el-table-column label="Actions" width="110" fixed="right">
          <template slot-scope="scope">
            <el-button
              v-if="scope.row.status === 'draft'"
              size="mini"
              type="primary"
              :loading="isPending(`publish:${idOf(scope.row)}`)"
              :disabled="anyPending"
              @click="publishPolicy(idOf(scope.row))"
            >
              Publish
            </el-button>
            <el-button
              v-if="scope.row.status === 'published'"
              size="mini"
              type="danger"
              plain
              :loading="isPending(`retire:${idOf(scope.row)}`)"
              :disabled="anyPending"
              @click="retirePolicy(idOf(scope.row))"
            >
              Retire
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </section>

    <el-row :gutter="16">
      <el-col :xs="24" :xl="15">
        <section class="panel">
          <div class="panel-header">
            <div>
              <h3>Draft a policy version</h3>
              <p>
                Values resolve from least to most specific: global → type → item → partner → partner + type → partner + item.
                Only fields you set are stored; everything else is inherited.
              </p>
            </div>
            <el-button size="small" :disabled="anyPending" @click="resetEditor">Reset form</el-button>
          </div>

          <el-form label-position="top" size="small" class="policy-form" @submit.native.prevent>
            <div class="scope-grid">
              <el-form-item label="Scope" required>
                <el-select v-model="editor.scope" style="width: 100%">
                  <el-option v-for="scope in scopes" :key="scope.value" :label="scope.label" :value="scope.value" />
                </el-select>
              </el-form-item>
              <el-form-item v-if="currentScope.needsType" label="Purchase type" required>
                <el-select v-model="editor.purchase_type" style="width: 100%">
                  <el-option v-for="type in purchaseTypes" :key="type.value" :label="type.label" :value="type.value" />
                </el-select>
              </el-form-item>
              <el-form-item v-if="currentScope.needsItem" label="Item ID" required>
                <el-input v-model.trim="editor.item_id" maxlength="24" placeholder="24-character collection / bundle ID" class="mono-input" />
              </el-form-item>
              <el-form-item v-if="currentScope.needsAffiliate" label="Partner (affiliate) ID" required>
                <el-input v-model.trim="editor.affiliate_id" maxlength="24" placeholder="24-character user ID" class="mono-input" />
              </el-form-item>
            </div>
            <div class="muted scope-help">{{ currentScope.help }}</div>

            <div class="field-list">
              <div class="field-row" :class="{ inherited: fields.one_time_rate_bps.inherit }">
                <div class="field-meta">
                  <div class="field-label">One-time purchase rate</div>
                  <div class="field-help">Commission share of net receipts for collection and bundle purchases.</div>
                </div>
                <div class="field-control">
                  <el-checkbox v-model="fields.one_time_rate_bps.inherit">Inherit</el-checkbox>
                  <template v-if="!fields.one_time_rate_bps.inherit">
                    <el-input-number v-model="fields.one_time_rate_bps.value" :min="0" :max="100" :precision="2" :step="1" controls-position="right" class="num-input" />
                    <span class="unit">%</span>
                  </template>
                </div>
              </div>

              <div class="field-row" :class="{ inherited: fields.monthly_rates_bps.inherit }">
                <div class="field-meta">
                  <div class="field-label">Monthly subscription rates</div>
                  <div class="field-help">One rate per paid month of a monthly subscription (months 1–12). Month 13 onward earns nothing.</div>
                </div>
                <div class="field-control column">
                  <el-checkbox v-model="fields.monthly_rates_bps.inherit">Inherit</el-checkbox>
                  <template v-if="!fields.monthly_rates_bps.inherit">
                    <div class="monthly-tools">
                      <el-input-number v-model="monthlyFill" :min="0" :max="100" :precision="2" :step="1" controls-position="right" size="mini" class="num-input small" />
                      <span class="unit">%</span>
                      <el-button size="mini" @click="fillMonthly">Fill all</el-button>
                      <el-button size="mini" @click="applyDiminishing">Diminishing preset</el-button>
                      <span class="muted">50, 45, 40, 35, 30, 25, 20, 15, 10, 10, 5, 5 %</span>
                    </div>
                    <div class="monthly-grid">
                      <div v-for="month in 12" :key="month" class="monthly-slot">
                        <div class="slot-label">Month {{ month }}</div>
                        <el-input-number
                          v-model="fields.monthly_rates_bps.value[month - 1]"
                          :min="0"
                          :max="100"
                          :precision="2"
                          :controls="false"
                          size="mini"
                          class="slot-input"
                        />
                      </div>
                    </div>
                  </template>
                </div>
              </div>

              <div class="field-row" :class="{ inherited: fields.annual_rate_bps.inherit }">
                <div class="field-meta">
                  <div class="field-label">Annual subscription rate</div>
                  <div class="field-help">Commission share of the first annual payment.</div>
                </div>
                <div class="field-control">
                  <el-checkbox v-model="fields.annual_rate_bps.inherit">Inherit</el-checkbox>
                  <template v-if="!fields.annual_rate_bps.inherit">
                    <el-input-number v-model="fields.annual_rate_bps.value" :min="0" :max="100" :precision="2" :step="1" controls-position="right" class="num-input" />
                    <span class="unit">%</span>
                  </template>
                </div>
              </div>

              <div class="field-row" :class="{ inherited: fields.attribution_days.inherit }">
                <div class="field-meta">
                  <div class="field-label">Attribution window</div>
                  <div class="field-help">Days a referral stays attributable after the visit (1–90).</div>
                </div>
                <div class="field-control">
                  <el-checkbox v-model="fields.attribution_days.inherit">Inherit</el-checkbox>
                  <template v-if="!fields.attribution_days.inherit">
                    <el-input-number v-model="fields.attribution_days.value" :min="1" :max="90" :step="1" step-strictly controls-position="right" class="num-input" />
                    <span class="unit">days</span>
                  </template>
                </div>
              </div>

              <div class="field-row" :class="{ inherited: fields.hold_days.inherit }">
                <div class="field-meta">
                  <div class="field-label">Hold period</div>
                  <div class="field-help">Days a commission stays pending before it can be paid out (0–365).</div>
                </div>
                <div class="field-control">
                  <el-checkbox v-model="fields.hold_days.inherit">Inherit</el-checkbox>
                  <template v-if="!fields.hold_days.inherit">
                    <el-input-number v-model="fields.hold_days.value" :min="0" :max="365" :step="1" step-strictly controls-position="right" class="num-input" />
                    <span class="unit">days</span>
                  </template>
                </div>
              </div>

              <div class="field-row" :class="{ inherited: fields.payout_minimum_cents.inherit }">
                <div class="field-meta">
                  <div class="field-label">Payout minimum</div>
                  <div class="field-help">Minimum available USD balance before a payout batch can be prepared.</div>
                </div>
                <div class="field-control">
                  <el-checkbox v-model="fields.payout_minimum_cents.inherit">Inherit</el-checkbox>
                  <template v-if="!fields.payout_minimum_cents.inherit">
                    <span class="unit">$</span>
                    <el-input-number v-model="fields.payout_minimum_cents.value" :min="0" :max="100000" :precision="2" :step="10" controls-position="right" class="num-input" />
                    <span class="unit">USD</span>
                  </template>
                </div>
              </div>

              <div class="field-row" :class="{ inherited: fields.payout_frequency.inherit }">
                <div class="field-meta">
                  <div class="field-label">Payout frequency</div>
                  <div class="field-help">How often payouts are scheduled for partners.</div>
                </div>
                <div class="field-control">
                  <el-checkbox v-model="fields.payout_frequency.inherit">Inherit</el-checkbox>
                  <el-radio-group v-if="!fields.payout_frequency.inherit" v-model="fields.payout_frequency.value">
                    <el-radio-button label="monthly">Monthly</el-radio-button>
                    <el-radio-button label="quarterly">Quarterly</el-radio-button>
                  </el-radio-group>
                </div>
              </div>

              <div class="field-row" :class="{ inherited: fields.fee_policy.inherit }">
                <div class="field-meta">
                  <div class="field-label">Transaction-fee deduction</div>
                  <div class="field-help">Deduct the actual Stripe fees (all five categories) from net receipts, or none.</div>
                </div>
                <div class="field-control">
                  <el-checkbox v-model="fields.fee_policy.inherit">Inherit</el-checkbox>
                  <el-radio-group v-if="!fields.fee_policy.inherit" v-model="fields.fee_policy.value">
                    <el-radio-button label="all">All fees</el-radio-button>
                    <el-radio-button label="none">No fees</el-radio-button>
                  </el-radio-group>
                </div>
              </div>

              <div class="field-row" :class="{ inherited: fields.fee_deduction_bps.inherit }">
                <div class="field-meta">
                  <div class="field-label">Fee deduction share</div>
                  <div class="field-help">Share of the deductible fees that is subtracted (100% = full fees).</div>
                </div>
                <div class="field-control">
                  <el-checkbox v-model="fields.fee_deduction_bps.inherit">Inherit</el-checkbox>
                  <template v-if="!fields.fee_deduction_bps.inherit">
                    <el-input-number v-model="fields.fee_deduction_bps.value" :min="0" :max="100" :precision="2" :step="5" controls-position="right" class="num-input" />
                    <span class="unit">%</span>
                  </template>
                </div>
              </div>

              <div class="field-row" :class="{ inherited: fields.offer.inherit }">
                <div class="field-meta">
                  <div class="field-label">Buyer offer</div>
                  <div class="field-help">Discount a referred buyer receives. Amounts are in USD.</div>
                </div>
                <div class="field-control column">
                  <el-checkbox v-model="fields.offer.inherit">Inherit</el-checkbox>
                  <div v-if="!fields.offer.inherit" class="offer-editor">
                    <el-radio-group v-model="offer.type" size="small">
                      <el-radio-button label="none">No offer</el-radio-button>
                      <el-radio-button label="percent">Percent off</el-radio-button>
                      <el-radio-button label="amount">Amount off</el-radio-button>
                      <el-radio-button label="fixed_price">Fixed price</el-radio-button>
                    </el-radio-group>

                    <div v-if="offer.type !== 'none'" class="offer-grid">
                      <div class="offer-cell">
                        <div class="slot-label">{{ offerValueLabel }}</div>
                        <span v-if="offer.type !== 'percent'" class="unit">$</span>
                        <el-input-number
                          v-model="offer.value"
                          :min="0"
                          :max="offer.type === 'percent' ? 100 : 100000"
                          :precision="2"
                          :step="1"
                          controls-position="right"
                          class="num-input"
                        />
                        <span class="unit">{{ offer.type === 'percent' ? '%' : 'USD' }}</span>
                      </div>
                      <div class="offer-cell">
                        <div class="slot-label">Discounted periods</div>
                        <el-input-number v-model="offer.periods" :min="1" :max="12" :step="1" step-strictly controls-position="right" class="num-input" />
                        <div class="muted cell-help">Billing periods for subscriptions (1–12); one-time purchases use 1.</div>
                      </div>
                      <div class="offer-cell">
                        <div class="slot-label">Eligibility</div>
                        <el-select v-model="offer.eligibility" style="width: 230px">
                          <el-option label="First one-time purchase" value="first_one_time" />
                          <el-option label="First subscription" value="first_subscription" />
                          <el-option label="Every referred purchase" value="all" />
                        </el-select>
                      </div>
                      <div class="offer-cell">
                        <div class="slot-label">Starts (optional)</div>
                        <el-date-picker v-model="offer.starts_at" type="datetime" placeholder="No start limit" style="width: 200px" />
                      </div>
                      <div class="offer-cell">
                        <div class="slot-label">Ends (optional)</div>
                        <el-date-picker v-model="offer.ends_at" type="datetime" placeholder="No end limit" style="width: 200px" />
                      </div>
                      <div class="offer-cell">
                        <div class="slot-label">Max redemptions (optional)</div>
                        <el-input-number v-model="offer.max_redemptions" :min="1" :max="10000000" :step="1" step-strictly controls-position="right" placeholder="No limit" class="num-input" />
                      </div>
                      <div class="offer-cell">
                        <div class="slot-label">Discount budget (optional)</div>
                        <span class="unit">$</span>
                        <el-input-number v-model="offer.budget_usd" :min="0.01" :max="1000000" :precision="2" :step="100" controls-position="right" placeholder="No limit" class="num-input" />
                        <span class="unit">USD</span>
                      </div>
                      <div class="offer-cell">
                        <div class="slot-label">Cap group (optional)</div>
                        <el-input v-model.trim="offer.cap_group" maxlength="64" placeholder="e.g. launch_2026" style="width: 200px" />
                        <div class="muted cell-help">Versions with the same group share one counter. Needs a redemption limit or budget.</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="save-grid">
              <el-form-item label="Effective at" required>
                <el-date-picker v-model="editor.effective_at" type="datetime" :clearable="false" style="width: 220px" @change="effectiveTouched = true" />
                <el-button type="text" class="inline-link" @click="resetEffective">Reset to now + 60 s</el-button>
                <div class="muted cell-help">
                  {{ effectiveTouched ? 'Must not be more than 60 s in the past when saving.' : 'Left unchanged, it is recalculated as now + 60 s when you save.' }}
                  A draft never applies until it is published.
                </div>
              </el-form-item>
              <el-form-item label="Reason" required>
                <el-input v-model="editor.reason" type="textarea" :rows="2" maxlength="500" show-word-limit placeholder="Required: recorded in the affiliate audit log" />
              </el-form-item>
            </div>
            <div class="form-actions">
              <span class="muted">{{ overrideCount }} field{{ overrideCount === 1 ? '' : 's' }} set</span>
              <el-button type="primary" :loading="isPending('save')" :disabled="anyPending" @click="saveDraft">Save draft</el-button>
            </div>
          </el-form>
          <div v-if="lastDraft" class="draft-card">
            <div class="draft-title"><i class="el-icon-document-checked"></i> Draft saved</div>
            <div class="mono">{{ lastDraft.id }}</div>
            <div class="muted">{{ lastDraft.scopeText }} · effective {{ formatTime(lastDraft.effective_at, true) }}</div>
            <div class="muted">Fields: {{ lastDraft.fields.join(', ') }}</div>
            <div class="draft-actions">
              <el-button
                size="mini"
                type="primary"
                :loading="isPending(`publish:${lastDraft.id}`)"
                :disabled="anyPending || lastDraft.published"
                @click="publishPolicy(lastDraft.id)"
              >
                {{ lastDraft.published ? 'Published' : 'Publish this draft' }}
              </el-button>
              <el-button size="mini" icon="el-icon-document-copy" @click="copyText(lastDraft.id, 'Policy ID copied')">Copy ID</el-button>
            </div>
          </div>
        </section>
      </el-col>

      <el-col :xs="24" :xl="9">
        <section class="panel">
          <div class="panel-header">
            <div>
              <h3>Preview resolved policy</h3>
              <p>Shows the value that applies to a purchase and which published version supplied it.</p>
            </div>
          </div>
          <el-form label-position="top" size="small" class="preview-form" @submit.native.prevent="runPreview">
            <el-form-item label="Purchase type" required>
              <el-select v-model="previewForm.purchase_type" style="width: 100%">
                <el-option v-for="type in purchaseTypes" :key="type.value" :label="type.label" :value="type.value" />
              </el-select>
            </el-form-item>
            <el-form-item label="Partner ID (optional)">
              <el-input v-model.trim="previewForm.affiliate_id" maxlength="24" clearable placeholder="24-character user ID" class="mono-input" />
            </el-form-item>
            <el-form-item label="Item ID (optional)">
              <el-input v-model.trim="previewForm.item_id" maxlength="24" clearable placeholder="24-character item ID" class="mono-input" />
            </el-form-item>
            <el-form-item label="At (optional)">
              <el-date-picker v-model="previewForm.at" type="datetime" placeholder="Now" style="width: 100%" />
            </el-form-item>
            <el-button type="primary" native-type="submit" :loading="previewLoading" icon="el-icon-view">Preview</el-button>
          </el-form>

          <template v-if="preview">
            <el-table :data="previewRows" border size="small" class="preview-table">
              <el-table-column label="Field" width="150">
                <template slot-scope="scope"><span class="strong">{{ scope.row.label }}</span></template>
              </el-table-column>
              <el-table-column label="Value" min-width="170">
                <template slot-scope="scope"><span class="preview-value">{{ scope.row.value }}</span></template>
              </el-table-column>
              <el-table-column label="Source" min-width="150">
                <template slot-scope="scope">
                  <el-tag v-if="scope.row.source === 'global_default'" size="mini" type="info">Built-in default</el-tag>
                  <div v-else class="source-cell">
                    <span class="mono">{{ scope.row.source || '-' }}</span>
                    <el-button v-if="scope.row.source" type="text" size="mini" icon="el-icon-document-copy" @click="copyText(scope.row.source, 'Policy ID copied')" />
                    <el-button
                      v-if="scope.row.source"
                      type="text"
                      size="mini"
                      :disabled="anyPending"
                      @click="retirePolicy(scope.row.source)"
                    >
                      Retire…
                    </el-button>
                  </div>
                </template>
              </el-table-column>
            </el-table>

            <div v-if="preview.offer_cap" class="cap-block">
              <div class="strong">Offer cap usage</div>
              <div class="muted mono">{{ preview.offer_cap.key }}</div>
              <div class="cap-row">
                <span>Redemptions</span>
                <span>{{ capUsage.redemptions }} / {{ preview.offer_cap.max_redemptions === null || preview.offer_cap.max_redemptions === undefined ? 'no limit' : preview.offer_cap.max_redemptions }}</span>
              </div>
              <el-progress v-if="capPercent('redemptions') !== null" :percentage="capPercent('redemptions')" :stroke-width="6" :show-text="false" />
              <div class="cap-row">
                <span>Discount granted</span>
                <span>{{ formatUsdCents(capUsage.discount_cents) }} / {{ preview.offer_cap.budget_cents === null || preview.offer_cap.budget_cents === undefined ? 'no budget' : formatUsdCents(preview.offer_cap.budget_cents) }}</span>
              </div>
              <el-progress v-if="capPercent('budget') !== null" :percentage="capPercent('budget')" :stroke-width="6" :show-text="false" />
            </div>
          </template>
        </section>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import {
  listAffiliatePolicies,
  previewAffiliatePolicy,
  publishAffiliatePolicy,
  retireAffiliatePolicy,
  saveAffiliatePolicy
} from '@/apis/lfAdmin'
import affiliateMixin, { OBJECT_ID_PATTERN, PURCHASE_TYPES, labelFor } from './affiliateMixin'

const FEE_CATEGORIES = ['processing', 'payment_method', 'cross_border', 'conversion', 'per_payment_service']
const DIMINISHING_PERCENT = [50, 45, 40, 35, 30, 25, 20, 15, 10, 10, 5, 5]
const CAP_GROUP_PATTERN = /^[a-z0-9_-]{1,64}$/
// Same shape the backend accepts for adminListAffiliatePolicies scope_key.
const SCOPE_KEY_PATTERN = /^[a-z_]+(:[a-z0-9_]+)*$/i

const VERSION_STATUSES = [
  { value: 'draft', label: 'Draft', tag: 'warning' },
  { value: 'published', label: 'Published', tag: 'success' },
  { value: 'retired', label: 'Retired', tag: 'info' }
]

const SCOPES = [
  { value: 'global', label: 'Global', help: 'Applies to every partner and purchase type.', needsType: false, needsItem: false, needsAffiliate: false },
  { value: 'type', label: 'Purchase type', help: 'Applies to one purchase type for every partner.', needsType: true, needsItem: false, needsAffiliate: false },
  { value: 'item', label: 'Item', help: 'Applies to one collection or bundle (within a purchase type) for every partner.', needsType: true, needsItem: true, needsAffiliate: false },
  { value: 'affiliate', label: 'Partner', help: 'Applies to one partner across every purchase type.', needsType: false, needsItem: false, needsAffiliate: true },
  { value: 'affiliate_type', label: 'Partner + purchase type', help: 'Applies to one partner for one purchase type.', needsType: true, needsItem: false, needsAffiliate: true },
  { value: 'affiliate_item', label: 'Partner + item', help: 'Applies to one partner for one item.', needsType: true, needsItem: true, needsAffiliate: true }
]

const FIELD_ORDER = [
  'one_time_rate_bps', 'monthly_rates_bps', 'annual_rate_bps', 'attribution_days', 'hold_days',
  'payout_minimum_cents', 'payout_frequency', 'fee_policy', 'fee_deduction_bps', 'offer'
]

const FIELD_LABELS = {
  one_time_rate_bps: 'One-time rate',
  monthly_rates_bps: 'Monthly rates',
  annual_rate_bps: 'Annual rate',
  attribution_days: 'Attribution',
  hold_days: 'Hold',
  payout_minimum_cents: 'Payout minimum',
  payout_frequency: 'Payout frequency',
  fee_policy: 'Fee deduction',
  fee_deduction_bps: 'Fee deduction share',
  offer: 'Buyer offer'
}

const ELIGIBILITY_LABELS = [
  { value: 'first_one_time', label: 'first one-time purchase' },
  { value: 'first_subscription', label: 'first subscription' },
  { value: 'all', label: 'every referred purchase' }
]

function defaultOffer(purchaseType = 'collection') {
  return {
    type: 'none',
    value: 10,
    periods: 1,
    eligibility: ['monthly', 'annual'].includes(purchaseType) ? 'first_subscription' : 'first_one_time',
    starts_at: null,
    ends_at: null,
    max_redemptions: undefined,
    budget_usd: undefined,
    cap_group: ''
  }
}

// Defaults mirror the backend launch defaults so an override starts from a sensible value.
function defaultEditor() {
  return {
    scope: 'global',
    purchase_type: 'collection',
    item_id: '',
    affiliate_id: '',
    fields: {
      one_time_rate_bps: { inherit: true, value: 60 },
      monthly_rates_bps: { inherit: true, value: new Array(12).fill(30) },
      annual_rate_bps: { inherit: true, value: 30 },
      attribution_days: { inherit: true, value: 30 },
      hold_days: { inherit: true, value: 30 },
      payout_minimum_cents: { inherit: true, value: 50 },
      payout_frequency: { inherit: true, value: 'monthly' },
      fee_policy: { inherit: true, value: 'all' },
      fee_deduction_bps: { inherit: true, value: 100 },
      offer: { inherit: true, value: defaultOffer() }
    },
    effective_at: new Date(Date.now() + 60 * 1000),
    reason: ''
  }
}

function isBlank(value) {
  return value === null || typeof value === 'undefined' || value === '' || Number.isNaN(Number(value))
}

function toUnix(date) {
  return Math.floor(new Date(date).getTime() / 1000)
}

export default {
  name: 'AffiliatePoliciesTab',
  mixins: [affiliateMixin],
  data() {
    return {
      scopes: SCOPES,
      purchaseTypes: PURCHASE_TYPES,
      editor: defaultEditor(),
      effectiveTouched: false,
      monthlyFill: 30,
      lastDraft: null,
      versionFilters: {
        status: 'all',
        scope_key: '',
        limit: 100
      },
      versionsLoading: false,
      versions: [],
      previewForm: {
        purchase_type: 'collection',
        affiliate_id: '',
        item_id: '',
        at: null
      },
      previewLoading: false,
      preview: null
    }
  },
  computed: {
    fields() {
      return this.editor.fields
    },
    offer() {
      return this.editor.fields.offer.value
    },
    currentScope() {
      return SCOPES.find((scope) => scope.value === this.editor.scope) || SCOPES[0]
    },
    overrideCount() {
      return FIELD_ORDER.filter((key) => !this.fields[key].inherit).length
    },
    offerValueLabel() {
      if (this.offer.type === 'percent') return 'Discount'
      if (this.offer.type === 'amount') return 'Amount off'
      return 'Promotional price'
    },
    previewRows() {
      if (!this.preview || !this.preview.values) return []
      const values = this.preview.values
      const sources = this.preview.sources || {}
      return FIELD_ORDER.filter((key) => key in values).map((key) => ({
        key,
        label: FIELD_LABELS[key],
        value: this.describeValue(key, values[key]),
        source: sources[key]
      }))
    },
    capUsage() {
      const cap = this.preview && this.preview.offer_cap
      return (cap && cap.usage) || { redemptions: 0, discount_cents: 0 }
    }
  },
  watch: {
    'editor.purchase_type'(type) {
      if (this.offer.eligibility !== 'all') {
        this.offer.eligibility = ['monthly', 'annual'].includes(type) ? 'first_subscription' : 'first_one_time'
      }
    }
  },
  mounted() {
    this.fetchVersions()
  },
  methods: {
    validId(id) {
      return OBJECT_ID_PATTERN.test(String(id || ''))
    },
    resetEditor() {
      this.editor = defaultEditor()
      this.effectiveTouched = false
    },
    resetEffective() {
      this.editor.effective_at = new Date(Date.now() + 60 * 1000)
      this.effectiveTouched = false
    },
    fillMonthly() {
      if (isBlank(this.monthlyFill)) return
      this.fields.monthly_rates_bps.value = new Array(12).fill(Number(this.monthlyFill))
    },
    applyDiminishing() {
      this.fields.monthly_rates_bps.value = DIMINISHING_PERCENT.slice()
    },
    percentToBps(value, label, errors) {
      if (isBlank(value)) {
        errors.push(`${label}: enter a percentage.`)
        return null
      }
      const bps = Math.round(Number(value) * 100)
      if (bps < 0 || bps > 10000) {
        errors.push(`${label}: must be between 0% and 100%.`)
        return null
      }
      return bps
    },
    usdToCents(value, label, errors, min = 0) {
      if (isBlank(value)) {
        errors.push(`${label}: enter an amount.`)
        return null
      }
      const cents = Math.round(Number(value) * 100)
      if (cents < min) {
        errors.push(`${label}: must be at least ${this.formatUsdCents(min)}.`)
        return null
      }
      return cents
    },
    wholeNumber(value, label, errors, min, max) {
      const number = Number(value)
      if (isBlank(value) || !Number.isInteger(number) || number < min || number > max) {
        errors.push(`${label}: enter a whole number from ${min} to ${max}.`)
        return null
      }
      return number
    },
    buildOffer(errors) {
      const offer = this.offer
      if (offer.type === 'none') return { type: 'none' }
      const result = { type: offer.type }
      if (offer.type === 'percent') {
        result.value = this.percentToBps(offer.value, 'Offer discount', errors)
      } else {
        result.value = this.usdToCents(offer.value, offer.type === 'amount' ? 'Offer amount' : 'Offer price', errors, offer.type === 'fixed_price' ? 1 : 0)
        result.currency = 'USD'
      }
      result.periods = this.wholeNumber(offer.periods, 'Offer periods', errors, 1, 12)
      result.eligibility = offer.eligibility
      if (offer.starts_at) result.starts_at = toUnix(offer.starts_at)
      if (offer.ends_at) result.ends_at = toUnix(offer.ends_at)
      if (result.starts_at !== undefined && result.ends_at !== undefined && result.starts_at >= result.ends_at) {
        errors.push('Offer end must be after its start.')
      }
      if (!isBlank(offer.max_redemptions)) {
        result.max_redemptions = this.wholeNumber(offer.max_redemptions, 'Max redemptions', errors, 1, 10000000)
      }
      if (!isBlank(offer.budget_usd)) {
        result.budget_cents = this.usdToCents(offer.budget_usd, 'Discount budget', errors, 1)
        if (result.budget_cents !== null && result.budget_cents > 100000000) errors.push('Discount budget: must be at most $1,000,000.00.')
      }
      const group = String(offer.cap_group || '').trim()
      if (group) {
        if (!CAP_GROUP_PATTERN.test(group)) errors.push('Cap group: use 1–64 lowercase letters, digits, "_" or "-".')
        if (result.max_redemptions === undefined && result.budget_cents === undefined) errors.push('Cap group needs a redemption limit or a discount budget.')
        result.cap_group = group
      }
      return result
    },
    buildRequest() {
      const errors = []
      const editor = this.editor
      const scope = this.currentScope
      const payload = { scope: scope.value }
      if (scope.needsType) payload.purchase_type = editor.purchase_type
      if (scope.needsItem) {
        if (!this.validId(editor.item_id)) errors.push('Item ID must be a 24-character hex ID.')
        payload.item_id = editor.item_id
      }
      if (scope.needsAffiliate) {
        if (!this.validId(editor.affiliate_id)) errors.push('Partner ID must be a 24-character hex ID.')
        payload.affiliate_id = editor.affiliate_id
      }

      const fields = {}
      const f = editor.fields
      if (!f.one_time_rate_bps.inherit) fields.one_time_rate_bps = this.percentToBps(f.one_time_rate_bps.value, 'One-time rate', errors)
      if (!f.monthly_rates_bps.inherit) {
        fields.monthly_rates_bps = f.monthly_rates_bps.value.map((value, index) => this.percentToBps(value, `Month ${index + 1}`, errors))
      }
      if (!f.annual_rate_bps.inherit) fields.annual_rate_bps = this.percentToBps(f.annual_rate_bps.value, 'Annual rate', errors)
      if (!f.attribution_days.inherit) fields.attribution_days = this.wholeNumber(f.attribution_days.value, 'Attribution window', errors, 1, 90)
      if (!f.hold_days.inherit) fields.hold_days = this.wholeNumber(f.hold_days.value, 'Hold period', errors, 0, 365)
      if (!f.payout_minimum_cents.inherit) {
        fields.payout_minimum_cents = this.usdToCents(f.payout_minimum_cents.value, 'Payout minimum', errors)
        if (fields.payout_minimum_cents !== null && fields.payout_minimum_cents > 10000000) errors.push('Payout minimum: must be at most $100,000.00.')
      }
      if (!f.payout_frequency.inherit) fields.payout_frequency = f.payout_frequency.value
      if (!f.fee_policy.inherit) fields.fee_policy = f.fee_policy.value === 'all' ? FEE_CATEGORIES.slice() : []
      if (!f.fee_deduction_bps.inherit) fields.fee_deduction_bps = this.percentToBps(f.fee_deduction_bps.value, 'Fee deduction share', errors)
      if (!f.offer.inherit) fields.offer = this.buildOffer(errors)
      if (!Object.keys(fields).length) errors.push('Set at least one field; a policy version cannot inherit everything.')
      payload.fields = fields

      const now = Math.floor(Date.now() / 1000)
      const effectiveAt = this.effectiveTouched && editor.effective_at ? toUnix(editor.effective_at) : now + 60
      if (effectiveAt < now - 60) errors.push('Effective time is in the past; choose a later time or reset it to now + 60 s.')
      if (effectiveAt > now + 5 * 365 * 86400) errors.push('Effective time must be within 5 years.')
      payload.effective_at = effectiveAt

      const reason = String(editor.reason || '').trim()
      if (!reason) errors.push('Enter a reason.')
      payload.reason = reason
      return { payload, errors }
    },
    scopeText(payload) {
      const parts = [labelFor(SCOPES, payload.scope)]
      if (payload.purchase_type) parts.push(labelFor(PURCHASE_TYPES, payload.purchase_type))
      if (payload.item_id) parts.push(`item ${payload.item_id}`)
      if (payload.affiliate_id) parts.push(`partner ${payload.affiliate_id}`)
      return parts.join(' · ')
    },
    async saveDraft() {
      const { payload, errors } = this.buildRequest()
      if (errors.length) {
        this.$message.warning(errors[0])
        return
      }
      const confirmed = await this.confirmAction({
        title: 'Save policy draft',
        lines: [
          `Save a draft for ${this.scopeText(payload)}, effective ${this.formatTime(payload.effective_at, true)}?`,
          'The draft has no effect until you publish it. Request fields:',
          this.codeBlock(JSON.stringify(payload.fields, null, 2))
        ],
        confirmText: 'Save draft',
        type: 'info',
        wide: true
      })
      if (!confirmed) return
      const result = await this.runMutation('save', () => saveAffiliatePolicy(payload), {
        success: 'Policy draft saved',
        failure: 'Failed to save policy draft'
      })
      if (result && result.data && result.data.id) {
        this.lastDraft = {
          id: result.data.id,
          scopeText: this.scopeText(payload),
          effective_at: payload.effective_at,
          fields: Object.keys(payload.fields),
          published: false
        }
        this.fetchVersions()
      }
    },
    versionContext(id) {
      const row = this.versions.find((version) => this.idOf(version) === id)
      if (row) return `${this.scopeLabel(row.scope)} (${row.scope_key}) · effective ${this.formatTime(row.effective_at, true)}`
      if (this.lastDraft && this.lastDraft.id === id) {
        return `${this.lastDraft.scopeText} · effective ${this.formatTime(this.lastDraft.effective_at, true)}`
      }
      return ''
    },
    async publishPolicy(id) {
      if (!this.validId(id)) return
      const isLast = this.lastDraft && this.lastDraft.id === id
      const confirmed = await this.confirmAction({
        title: 'Publish policy',
        lines: [
          `Publish policy draft ${id}?`,
          this.versionContext(id),
          'Once published it applies to referrals from its effective time (immediately if that time has passed). Published versions cannot be edited; retire and publish a new version instead.'
        ],
        confirmText: 'Publish'
      })
      if (!confirmed) return
      const result = await this.runMutation(`publish:${id}`, () => publishAffiliatePolicy({ id }), {
        success: 'Policy published',
        failure: 'Failed to publish policy'
      })
      if (result) {
        if (isLast) this.lastDraft.published = true
        this.fetchVersions()
      }
    },
    async retirePolicy(id) {
      if (!this.validId(id)) return
      const reason = await this.confirmWithReason({
        title: 'Retire policy',
        lines: [
          `Retire published policy ${id}?`,
          this.versionContext(id),
          'Future referrals for its scope fall back to less specific policies. Earlier versions of the same scope are not restored.'
        ],
        confirmText: 'Retire',
        type: 'error'
      })
      if (reason === null) return
      const result = await this.runMutation(`retire:${id}`, () => retireAffiliatePolicy({ id, reason }), {
        success: 'Policy retired',
        failure: 'Failed to retire policy'
      })
      if (result) this.fetchVersions()
    },
    async fetchVersions() {
      const params = { limit: this.versionFilters.limit }
      if (this.versionFilters.status !== 'all') params.status = this.versionFilters.status
      const scopeKey = this.versionFilters.scope_key
      if (scopeKey) {
        if (!SCOPE_KEY_PATTERN.test(scopeKey)) {
          this.$message.warning('Scope key looks like "global", "type:collection" or "affiliate:<id>".')
          return
        }
        params.scope_key = scopeKey
      }
      this.versionsLoading = true
      const data = await this.loadData(() => listAffiliatePolicies(params), 'Failed to load policy versions')
      if (data !== undefined) this.versions = Array.isArray(data) ? data : []
      this.versionsLoading = false
    },
    editorScopeKey() {
      const editor = this.editor
      const scope = this.currentScope
      if (scope.needsItem && !this.validId(editor.item_id)) return ''
      if (scope.needsAffiliate && !this.validId(editor.affiliate_id)) return ''
      const type = editor.purchase_type
      const map = {
        global: 'global',
        type: `type:${type}`,
        item: `item:${type}:${editor.item_id}`,
        affiliate: `affiliate:${editor.affiliate_id}`,
        affiliate_type: `affiliate_type:${editor.affiliate_id}:${type}`,
        affiliate_item: `affiliate_item:${editor.affiliate_id}:${type}:${editor.item_id}`
      }
      return map[scope.value] || ''
    },
    useEditorScope() {
      const key = this.editorScopeKey()
      if (!key) {
        this.$message.warning('Complete the scope IDs in the editor first.')
        return
      }
      this.versionFilters.scope_key = key
      this.fetchVersions()
    },
    scopeLabel(scope) {
      return labelFor(SCOPES, scope)
    },
    versionStatusLabel(status) {
      return labelFor(VERSION_STATUSES, status)
    },
    versionStatusTag(status) {
      const match = VERSION_STATUSES.find((option) => option.value === status)
      return match ? match.tag : ''
    },
    versionRowClass({ row }) {
      return this.lastDraft && this.idOf(row) === this.lastDraft.id ? 'is-last-draft' : ''
    },
    isFuture(ts) {
      return Number(ts || 0) * 1000 > Date.now()
    },
    fieldRows(fields) {
      if (!fields || typeof fields !== 'object') return []
      return FIELD_ORDER.filter((key) => key in fields).map((key) => ({
        key,
        label: FIELD_LABELS[key],
        value: this.describeValue(key, fields[key])
      }))
    },
    async runPreview() {
      const form = this.previewForm
      const params = { purchase_type: form.purchase_type }
      if (form.affiliate_id) {
        if (!this.validId(form.affiliate_id)) {
          this.$message.warning('Partner ID must be a 24-character hex ID.')
          return
        }
        params.affiliate_id = form.affiliate_id
      }
      if (form.item_id) {
        if (!this.validId(form.item_id)) {
          this.$message.warning('Item ID must be a 24-character hex ID.')
          return
        }
        params.item_id = form.item_id
      }
      if (form.at) params.at = toUnix(form.at)
      this.previewLoading = true
      const data = await this.loadData(() => previewAffiliatePolicy(params), 'Failed to preview policy')
      if (data !== undefined) this.preview = data || null
      this.previewLoading = false
    },
    capPercent(kind) {
      const cap = this.preview && this.preview.offer_cap
      if (!cap) return null
      const limit = kind === 'budget' ? cap.budget_cents : cap.max_redemptions
      if (limit === null || limit === undefined || Number(limit) <= 0) return null
      const used = kind === 'budget' ? this.capUsage.discount_cents : this.capUsage.redemptions
      return Math.min(100, Math.round((Number(used || 0) / Number(limit)) * 100))
    },
    describeOffer(offer) {
      if (!offer || offer.type === 'none') return 'No buyer offer'
      const parts = []
      if (offer.type === 'percent') parts.push(`${this.formatBps(offer.value)} off`)
      else if (offer.type === 'amount') parts.push(`${this.formatUsdCents(offer.value)} off`)
      else parts.push(`Fixed price ${this.formatUsdCents(offer.value)}`)
      const periods = Number(offer.periods || 1)
      parts.push(`${periods} period${periods === 1 ? '' : 's'}`)
      if (offer.eligibility) parts.push(labelFor(ELIGIBILITY_LABELS, offer.eligibility))
      if (offer.starts_at) parts.push(`from ${this.formatTime(offer.starts_at)}`)
      if (offer.ends_at) parts.push(`until ${this.formatTime(offer.ends_at)}`)
      if (offer.max_redemptions) parts.push(`max ${offer.max_redemptions} redemptions`)
      if (offer.budget_cents) parts.push(`budget ${this.formatUsdCents(offer.budget_cents)}`)
      if (offer.cap_group) parts.push(`cap group ${offer.cap_group}`)
      return parts.join(' · ')
    },
    describeValue(key, value) {
      if (value === null || value === undefined) return '-'
      if (['one_time_rate_bps', 'annual_rate_bps', 'fee_deduction_bps'].includes(key)) return this.formatBps(value)
      if (key === 'monthly_rates_bps') return Array.isArray(value) ? value.map((bps) => this.formatBps(bps)).join(', ') : '-'
      if (key === 'attribution_days' || key === 'hold_days') return `${value} days`
      if (key === 'payout_minimum_cents') return this.formatUsdCents(value)
      if (key === 'payout_frequency') return value === 'quarterly' ? 'Quarterly' : 'Monthly'
      if (key === 'fee_policy') {
        if (!Array.isArray(value) || !value.length) return 'No fees deducted'
        return value.length === FEE_CATEGORIES.length ? 'All transaction fees' : value.join(', ')
      }
      if (key === 'offer') return this.describeOffer(value)
      return typeof value === 'object' ? JSON.stringify(value) : String(value)
    }
  }
}
</script>

<style scoped>
.panel {
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
  border: 1px solid #ebeef5;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}

.panel-header h3 {
  margin: 0;
  color: #303133;
  font-size: 16px;
}

.panel-header p {
  margin: 4px 0 0;
  color: #909399;
  font-size: 13px;
  line-height: 1.5;
}

.scope-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0 12px;
}

.scope-help {
  margin: -8px 0 12px;
}

.field-list {
  border: 1px solid #ebeef5;
  margin-bottom: 16px;
}

.field-row {
  display: grid;
  grid-template-columns: minmax(180px, 240px) 1fr;
  gap: 16px;
  padding: 12px 14px;
  border-bottom: 1px solid #ebeef5;
}

.field-row:last-child {
  border-bottom: none;
}

.field-row.inherited {
  background: #fafbfc;
}

.field-row.inherited .field-label {
  color: #909399;
}

.field-label {
  color: #303133;
  font-weight: 600;
  font-size: 13px;
}

.field-help,
.cell-help {
  margin-top: 2px;
  color: #909399;
  font-size: 12px;
  line-height: 1.4;
}

.field-control {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  min-width: 0;
}

.field-control.column {
  flex-direction: column;
  align-items: flex-start;
}

.num-input {
  width: 150px;
}

.num-input.small {
  width: 110px;
}

.unit {
  color: #606266;
  font-size: 13px;
}

.monthly-tools {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.monthly-tools .el-button + .el-button {
  margin-left: 0;
}

.monthly-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(72px, 1fr));
  gap: 8px;
  width: 100%;
}

.slot-label {
  margin-bottom: 4px;
  color: #606266;
  font-size: 12px;
}

.slot-input {
  width: 100%;
}

.offer-editor {
  width: 100%;
}

.offer-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 12px 16px;
  margin-top: 12px;
}

.offer-cell .unit {
  margin: 0 4px;
}

.save-grid {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) minmax(260px, 1.4fr);
  gap: 0 16px;
}

.inline-link {
  margin-left: 8px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
}

.versions-filters {
  margin-bottom: 4px;
}

.versions-table >>> .is-last-draft td {
  background: #f0f9eb;
}

.expand-body {
  padding: 4px 12px;
}

.expand-note {
  margin-top: 8px;
}

.cell-wrap {
  word-break: break-word;
}

.draft-card {
  padding: 12px;
  margin-top: 14px;
  background: #f0f9eb;
  border: 1px solid #e1f3d8;
}

.draft-title {
  margin-bottom: 4px;
  color: #67c23a;
  font-weight: 600;
}

.draft-actions {
  margin-top: 8px;
}

.preview-form {
  margin-bottom: 14px;
}

.preview-table {
  margin-bottom: 12px;
}

.preview-value {
  word-break: break-word;
}

.source-cell {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.source-cell .el-button + .el-button {
  margin-left: 0;
}

.cap-block {
  padding: 12px;
  background: #f5f7fa;
  border: 1px solid #ebeef5;
}

.cap-row {
  display: flex;
  justify-content: space-between;
  margin: 8px 0 4px;
  font-size: 13px;
}

.strong {
  color: #303133;
  font-weight: 600;
}

.muted {
  color: #909399;
  font-size: 12px;
}

.mono {
  font-family: Menlo, Consolas, monospace;
  font-size: 12px;
  word-break: break-all;
}

.mono-input >>> input {
  font-family: Menlo, Consolas, monospace;
}

@media (max-width: 900px) {
  .field-row,
  .save-grid {
    grid-template-columns: 1fr;
  }

  .monthly-grid {
    grid-template-columns: repeat(4, minmax(72px, 1fr));
  }
}
</style>
